<template>
  <div class="referral-program-report-page">
    <general-top-bar :title="$t('reports.referralProgramReport')"></general-top-bar>
    <div class="sales-report">
      <div class="row mar-bt-15 referral-program-report-filters">
        <div class="col-md-6">
          <div class="mb-3 w-100">
            <label class="form-label text-start w-100">{{ $t('referralProgram.filterByCampaign') }}</label>
            <select class="form-control" v-model="selectedCampaignId" @change="onCampaignFilterChange">
              <option value="">{{ $t('general.all') }}</option>
              <option
                v-for="c in campaigns"
                :key="c.id"
                :value="c.id"
              >
                {{ c.name || $t('general.idHash', { id: c.id }) }}
              </option>
            </select>
          </div>
        </div>
        <div class="col-md-6">
          <div class="mb-3 w-100">
            <label class="form-label text-start w-100">{{ $t('referralProgram.filterByAmbassador') }}</label>
            <div class="referral-program-report-filters__ambassador-row">
              <el-select
                v-model="selectedAmbassadorId"
                filterable
                clearable
                :placeholder="$t('general.search')"
                :loading="loadingAmbassadorOptions"
              >
                <el-option
                  v-for="item in ambassadorOptions"
                  :key="'amb-' + ambassadorOptionId(item)"
                  :label="ambassadorOptionLabel(item)"
                  :value="ambassadorOptionId(item)"
                />
              </el-select>
              <div
                :title="$t('reports.search')"
                class="btn btn-icon btn-info btn-search referral-program-report-filters__search-btn"
                @click="applyFilters"
              >
                <i class="ti-search"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-12">
        <div class="card">
          <div class="card-content row">
            <data-tables-server
              class="campaign-table width-table referral-program-report__table"
              @query-change="onAmbassadorsPage"
              v-loading="loading"
              :data="ambassadorsRows"
              :total="ambassadorsTotal"
              :pagination-props="paginationProps"
              :table-props="{ border: false }"
              style="width: 100%"
              @row-click="onAmbassadorRow"
            >
              <el-table-column class-name="centered-cell" align="center" header-align="center" :min-width="70" prop="ambassador_user_id" :label="$t('referralProgram.ambassadorId')">
                <template slot-scope="p">
                  <span class="referral-report-table__ellipsis">{{ p.row.ambassador_user_id || p.row.user_id || p.row.id }}</span>
                </template>
              </el-table-column>
              <el-table-column class-name="centered-cell" align="center" header-align="center" :min-width="160" :label="$t('reports.name')">
                <template slot-scope="p">
                  <span
                    class="referral-report-table__ellipsis"
                    :title="displayPersonName(p.row, 'ambassador')"
                  >{{ displayPersonName(p.row, 'ambassador') }}</span>
                </template>
              </el-table-column>
              <el-table-column class-name="centered-cell" align="center" header-align="center" :min-width="180" :label="$t('reports.email')">
                <template slot-scope="p">
                  <span
                    class="referral-report-table__ellipsis"
                    :title="displayPersonEmail(p.row) || $t('general.emDash')"
                  >{{ displayPersonEmail(p.row) || $t('general.emDash') }}</span>
                </template>
              </el-table-column>
              <el-table-column class-name="centered-cell" align="center" header-align="center" :min-width="160" prop="campaign.name" :label="$t('reports.campaign')">
                <template slot-scope="p">
                  <span class="referral-report-table__ellipsis" :title="campaignNameForRow(p.row)">{{ campaignNameForRow(p.row) }}</span>
                </template>
              </el-table-column>
              <el-table-column class-name="centered-cell" align="center" header-align="center" :min-width="100" :label="$t('referralProgram.refereesCount')">
                <template slot-scope="p">
                  {{ ambassadorRefereesCount(p.row) }}
                </template>
              </el-table-column>
              <el-table-column class-name="centered-cell" align="center" header-align="center" :min-width="120" :label="$t('referralProgram.totalRewards')">
                <template slot-scope="p">
                  {{ formatMoney(ambassadorEarningsTotal(p.row)) }}
                  <small v-if="p.row.earnings && p.row.earnings.coupons_awarded" class="d-block">{{ $t('referralProgram.v2.couponsAwarded') }}: {{ p.row.earnings.coupons_awarded }}</small>
                </template>
              </el-table-column>
              <el-table-column class-name="centered-cell" align="center" header-align="center" :min-width="120" :label="$t('referralProgram.totalCashbackPercent')">
                <template slot-scope="p">
                  {{ formatMoney(ambassadorEarningsCashback(p.row)) }}
                </template>
              </el-table-column>
            </data-tables-server>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import { Select } from 'element-ui'
import DataTables from 'vue-data-tables'
import GeneralTopBar from 'src/components/UIComponents/Inputs/generalTopBar.vue'
import swal from 'sweetalert2'

Vue.use(DataTables)
Vue.use(Select)

export default {
  components: { GeneralTopBar },
  props: {
    isReferralProgramReport: { type: Boolean, default: false }
  },
  watch: {
    isReferralProgramReport (v) {
      if (v) {
        this.loadCampaigns()
        this.loadAmbassadorOptions()
        this.bootstrapAmbassadors()
      }
    }
  },
  computed: {
    ambassadorOptions () {
      const cacheKey = this.ambassadorOptionsCacheKey()
      return this.ambassadorOptionsCache[cacheKey] || []
    },
    campaignsById () {
      const map = {}
      ;(this.campaigns || []).forEach((c) => {
        if (c && c.id != null) map[String(c.id)] = c
      })
      return map
    }
  },
  data () {
    return {
      loading: false,
      loadingAmbassadorOptions: false,
      paginationProps: { layout: 'prev, pager, next, sizes, total' },
      ambassadorsRows: [],
      ambassadorsTotal: 0,
      campaigns: [],
      selectedCampaignId: '',
      selectedAmbassadorId: '',
      ambassadorOptionsCache: {},
      ambassadorsReportCache: {},
      pageSize: 10
    }
  },
  methods: {
    formatMoney (v) {
      if (v === null || v === undefined || v === '') return this.$t('general.emDash')
      const n = Number(v)
      return isNaN(n) ? String(v) : n.toFixed(2)
    },
    ambassadorRefereesCount (row) {
      if (row.registered_referees_count != null) return row.registered_referees_count
      if (row.referees_count != null) return row.referees_count
      if (row.referee_count != null) return row.referee_count
      return this.$t('general.emDash')
    },
    ambassadorEarningsTotal (row) {
      if (row.earnings && row.earnings.total != null) return row.earnings.total
      return row.total_cashback_and_order_rewards
    },
    ambassadorEarningsCashback (row) {
      if (row.earnings && row.earnings.from_cashback != null) return row.earnings.from_cashback
      return row.total_cashback_from_percent
    },
    pickField (row, key) {
      if (!row || key == null) return null
      const parts = String(key).split('.')
      let cur = row
      for (const part of parts) {
        if (cur == null || typeof cur !== 'object') return null
        cur = cur[part]
      }
      if (cur == null || cur === '') return null
      return cur
    },
    pickStringField (row, keys) {
      if (!row || !keys) return null
      for (const key of keys) {
        const v = this.pickField(row, key)
        if (v != null && String(v).trim() !== '') return String(v).trim()
      }
      return null
    },
    campaignNameById (id) {
      if (id == null || id === '') return null
      const match = this.campaignsById[String(id)]
      if (!match) return null
      const name = match.name != null ? String(match.name).trim() : ''
      return name || null
    },
    campaignObjectFromRow (row) {
      if (!row) return null
      const c = row.campaign || row.referral_campaign || row.referralCampaign
      if (c && typeof c === 'object') return c
      if (typeof c === 'string' && c.trim().startsWith('{')) {
        try {
          const parsed = JSON.parse(c.trim())
          if (parsed && typeof parsed === 'object') return parsed
        } catch (_) {}
      }
      return null
    },
    campaignNameForRow (row) {
      if (!row) return this.$t('general.emDash')

      const campaign = this.campaignObjectFromRow(row)
      if (campaign && campaign.name != null && String(campaign.name).trim() !== '') {
        return String(campaign.name).trim()
      }

      const campaignId = (campaign && campaign.id != null)
        ? campaign.id
        : (row.referral_campaign_id || row.campaign_id || null)
      const fromList = this.campaignNameById(campaignId)
      if (fromList) return fromList

      const flatName = this.pickStringField(row, ['referral_campaign_name', 'campaign_name'])
      if (flatName) return flatName

      if (campaignId != null && campaignId !== '') {
        return this.$t('general.idHash', { id: campaignId })
      }
      return this.$t('general.emDash')
    },
    displayPersonEmail (row) {
      return this.pickStringField(row, [
        'email',
        'user_email',
        'user.email',
        'ambassador.email',
        'ambassador_user.email',
        'ambassador_user_email'
      ])
    },
    displayPersonName (row, role) {
      if (!row) return this.$t('general.emDash')
      const nestedName = this.pickStringField(row, [
        'full_name',
        'name',
        'user.full_name',
        'user.name',
        'ambassador.full_name',
        'ambassador.name',
        'ambassador_user.full_name',
        'ambassador_user.name'
      ])
      if (nestedName) return nestedName
      const fn = String(
        row.firstname || row.first_name ||
        this.pickField(row, 'user.firstname') || this.pickField(row, 'user.first_name') ||
        this.pickField(row, 'ambassador.firstname') || this.pickField(row, 'ambassador.first_name') || ''
      ).trim()
      const ln = String(
        row.lastname || row.last_name ||
        this.pickField(row, 'user.lastname') || this.pickField(row, 'user.last_name') ||
        this.pickField(row, 'ambassador.lastname') || this.pickField(row, 'ambassador.last_name') || ''
      ).trim()
      const s = [fn, ln].filter(Boolean).join(' ')
      if (s) return s
      if (role === 'ambassador') {
        const id = row.ambassador_user_id || row.user_id || row.id
        return this.$t('general.idHash', { id })
      }
      const id = row.referee_user_id || row.user_id || row.id
      return this.$t('general.idHash', { id })
    },
    ambassadorIdFromRow (row) {
      return row.ambassador_user_id || row.user_id || row.id
    },
    onAmbassadorRow (row) {
      const ambassadorId = this.ambassadorIdFromRow(row)
      const title = this.displayPersonName(row, 'ambassador')
      this.$router.push({
        name: 'Referral Program Ambassador Drilldown',
        params: { ambassadorId: String(ambassadorId) },
        query: { title }
      })
    },
    normalizeRows (response) {
      if (!response || !response.data) return []
      const d = response.data
      return Array.isArray(d) ? d : (Array.isArray(d.data) ? d.data : [])
    },
    normalizeTotal (response) {
      if (response.pagination && response.pagination.total != null) return response.pagination.total
      if (response.meta && response.meta.total != null) return response.meta.total
      return this.normalizeRows(response).length
    },
    baseParams () {
      return {
        token: this.$session.get('token'),
        company_id: this.$session.get('selectedCompany')
      }
    },
    ambassadorOptionsCacheKey () {
      return String(this.selectedCampaignId || '')
    },
    ambassadorsReportCacheKey (page, perPage) {
      return JSON.stringify({
        campaign: this.selectedCampaignId || '',
        page,
        perPage
      })
    },
    hasAmbassadorFilter () {
      return this.selectedAmbassadorId != null && this.selectedAmbassadorId !== ''
    },
    campaignFilterParams () {
      const params = {}
      if (this.selectedCampaignId != null && this.selectedCampaignId !== '') {
        params.referral_campaign_id = this.selectedCampaignId
        params.campaign_id = this.selectedCampaignId
      }
      return params
    },
    filterParams () {
      const params = Object.assign({}, this.campaignFilterParams())
      if (this.hasAmbassadorFilter()) {
        params.ambassador_id = this.selectedAmbassadorId
      }
      return params
    },
    ambassadorOptionId (item) {
      if (!item) return ''
      return item.ambassador_user_id || item.user_id || item.id || ''
    },
    ambassadorOptionLabel (item) {
      const name = this.displayPersonName(item, 'ambassador')
      const id = this.ambassadorOptionId(item)
      if (name && !String(name).startsWith('#')) return name
      return id ? this.$t('general.idHash', { id }) : this.$t('general.emDash')
    },
    loadCampaigns () {
      if (!this.isReferralProgramReport) {
        return
      }
      this.apiRequest('/referral-campaign', 'GET', this.baseParams(), (response) => {
        if (response.status) {
          this.campaigns = this.normalizeRows(response)
          if (this.ambassadorsRows.length) {
            this.ambassadorsRows = this.ambassadorsRows.slice()
          }
        }
      })
    },
    loadAmbassadorOptions () {
      if (!this.isReferralProgramReport) {
        return
      }
      const cacheKey = this.ambassadorOptionsCacheKey()
      if (this.ambassadorOptionsCache[cacheKey]) {
        return
      }
      this.loadingAmbassadorOptions = true
      const data = Object.assign({}, this.baseParams(), this.campaignFilterParams())
      this.apiRequest('/reports/v2/referral-ambassadors-options', 'GET', data, (response) => {
        this.loadingAmbassadorOptions = false
        const rows = response.status ? this.normalizeRows(response) : []
        this.$set(this.ambassadorOptionsCache, cacheKey, rows)
      })
    },
    onCampaignFilterChange () {
      this.selectedAmbassadorId = ''
      this.loadAmbassadorOptions()
    },
    applyFilters () {
      this.fetchAmbassadors(1, this.pageSize)
    },
    bootstrapAmbassadors () {
      this.fetchAmbassadors(1, this.pageSize)
    },
    onAmbassadorsPage (q) {
      this.pageSize = q.pageSize
      this.fetchAmbassadors(q.page, q.pageSize)
    },
    fetchAmbassadors (page, perPage) {
      if (!this.isReferralProgramReport) {
        return
      }
      this.pageSize = perPage
      const cacheKey = this.ambassadorsReportCacheKey(page, perPage)
      if (!this.hasAmbassadorFilter() && this.ambassadorsReportCache[cacheKey]) {
        const cached = this.ambassadorsReportCache[cacheKey]
        this.ambassadorsRows = cached.rows
        this.ambassadorsTotal = cached.total
        return
      }

      this.loading = true
      const data = Object.assign({}, this.baseParams(), { page, perPage }, this.filterParams())
      this.apiRequest('/reports/v2/referral-ambassadors?page=' + page + '&perPage=' + perPage, 'GET', data, (response) => {
        if (response.status) {
          const rows = this.normalizeRows(response)
          const total = this.normalizeTotal(response)
          this.ambassadorsRows = rows
          this.ambassadorsTotal = total
          if (!this.hasAmbassadorFilter()) {
            this.$set(this.ambassadorsReportCache, cacheKey, { rows, total })
          }
        } else {
          swal('', this.$t('validations.wentWrong'), 'error')
        }
        this.loading = false
      })
    }
  }
}
</script>

<style scoped>
.referral-program-report-page >>> div.campaign-table div.pagination-bar {
  display: block !important;
}

.referral-program-report-page >>> .el-table .cell {
  overflow: hidden;
}

.referral-program-report-page >>> .el-table .cell > .referral-report-table__ellipsis {
  min-width: 0;
}

.referral-program-report-filters__ambassador-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.referral-program-report-filters__ambassador-row >>> .el-select {
  flex: 1;
  min-width: 0;
}

.referral-program-report-filters__search-btn {
  flex-shrink: 0;
  margin: 0;
}
</style>

<style>
.referral-program-report-page .centered-cell .cell {
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
  text-align: center !important;
}

.referral-program-report-page .centered-cell .cell .referral-report-table__ellipsis {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}
</style>



// WEBPACK FOOTER //
// src/components/Dashboard/Views/Reports/Versions/2/ReferralProgramReport.vue